export const SDK_STATUSES = {
    SUCCESS: "SUCCESS",
    CANCELLED: "CANCELLED",
} as const;

export const ORDER_STATUSES = {
    AUTHENTICATED: "authenticated",
    PROCESSING: "processing",
    PENDING: "pending",
    COMPLETE: "complete",
    FULFILLED: "fulfiled",
    VOID: "voided",
    REQUEST_TIMEOUT: "request_timeout",
    FULFILLMENT_ERROR: "fulfil_error",
    // Synthetic, client-only terminal status. Not returned by the service —
    // set by the poll loop when the order is still processing after
    // GC_ORDER_STATUS_DELAYED_NOTICE_TIMEOUT so we can show the "still
    // processing / don't re-order" notice instead of an indefinite spinner.
    FULFILLMENT_DELAYED: "fulfil_delayed",
};

export const POLLING_STATUS_CODES = [ORDER_STATUSES.AUTHENTICATED, ORDER_STATUSES.PROCESSING];

// The only statuses that confirm an order succeeded. A status that is neither in flight,
// a failure, nor one of these (e.g. `created`, or a 200 with no status) is not a
// confirmation; the status poll treats it as transient and keeps polling.
export const SUCCESS_STATUS_CODES = [ORDER_STATUSES.FULFILLED, ORDER_STATUSES.COMPLETE];

// Terminal error_code values a `fulfil_error` order status can carry. The
// PROMOTION_* codes are proposed (see docs/gift-cards/promo-failed/README.md):
// the order was cancelled because the promotional offer could not be applied,
// so the customer may re-order at the regular price.
export const PROMOTION_ERROR_CODES = {
    UNAVAILABLE: "PROMOTION_UNAVAILABLE",
    ALREADY_CLAIMED: "PROMOTION_ALREADY_CLAIMED",
} as const;

export type PROMOTION_ERROR_CODE = typeof PROMOTION_ERROR_CODES[keyof typeof PROMOTION_ERROR_CODES];

export const ERROR_CODES: string[] = [
    "OUT_OF_STOCK",
    "INVALID_RECIPIENT_EMAIL",
    ...Object.values(PROMOTION_ERROR_CODES),
];

/*
 * The gift card service is inconsistent about case on the wire: order_status values are
 * lowercase snake_case (`fulfil_error`) while error_code values are UPPER_SNAKE
 * (`OUT_OF_STOCK`), and the service's own API docs quote statuses in uppercase. Normalise
 * both at the boundary so every comparison against the constants above is case-insensitive.
 */
export const normalizeOrderStatus = (orderStatus: string | null | undefined): ORDER_STATUS | null =>
    orderStatus ? (orderStatus.trim().toLowerCase() as ORDER_STATUS) : null;

export const normalizeErrorCode = (errorCode: string | null | undefined): string | null =>
    errorCode ? errorCode.trim().toUpperCase() : null;

export const isPromotionErrorCode = (errorCode: string | null): errorCode is PROMOTION_ERROR_CODE =>
    Object.values<string>(PROMOTION_ERROR_CODES).includes(normalizeErrorCode(errorCode));

export const CHECKOUT_FIELDS = "gift_card_order_customer_fields";

export type ORDER_STATUS = typeof ORDER_STATUSES[keyof typeof ORDER_STATUSES];
